import type {
  CameraProps,
  GridLayout,
  PlaneBox,
} from '@Components/BigBrowse/Model/ModelTypes';
import type { ViewData } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/multisection.data';
import { initViewData } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/multisection.data';
import { postPopulationProcess } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/postPopulationProcess';
import {
  addGapBeforeSectionGeneral,
  addSections,
} from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/sections.common';
import { addFooter } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/footer/addFooter';
import {
  addPageBottomPadding,
  addPageTopPadding,
} from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/pagePaddings/MultiSectionView2.pagePaddings';
import { pipeAsyncTransformer } from '@Utils/pipeAsync';
import pipeWith from 'ramda/src/pipeWith';

import type { BrandConcessionContentDM } from './common/MultiSectionView.types';
import { calculateColumnsAndGap } from './common/columnCalculator';
import { addHeader } from './header/addHeader';

export async function getMultiSectionLayout(
  modelData: BrandConcessionContentDM,
  cameraProps: CameraProps
): Promise<GridLayout> {
  const viewData = await addLayoutContent({ modelData, cameraProps });
  const postData = await postPopulationProcess(viewData);

  return getLayout(postData);
}

const addLayoutContent = pipeWith(pipeAsyncTransformer, [
  // Here we create 'ViewData' object and pass it around to functions to add and modify its content.
  initViewData,
  calculateColumnsAndGap,
  // Padding added into heading
  // addPageTopPadding,
  addHeader,
  addSections,
  addGapBeforeSectionGeneral,
  addFooter,
  addPageBottomPadding,
]) as unknown as (
  ...args: Parameters<typeof initViewData> // Parameters of the first function
) => Promise<ViewData>; // The result of the last function wrapped in 'Promise'

function getLayout(viewData: ViewData): GridLayout {
  const { layoutTable, layoutWidth, layoutHeight } = viewData;
  const items = layoutTable!.flat();

  const { box } = viewData.cameraProps;

  const scrollBox: PlaneBox = {
    left: box.left,
    right: box.right,
    top: box.top,
    bottom: box.top - layoutHeight!,
  };

  return {
    items,
    size: { width: layoutWidth!, height: layoutHeight! },
    scrollBox,
  };
}
